import React, {
  useEffect, useState, useRef, useCallback,
} from 'lib/react';
import { useDispatch, useSelector } from 'lib/redux';
import { useTranslation } from 'lib/translation';
import {
  EVGrid,
  EVPaper,
  EVTypography,
  EVInputLabel,
  EVRadio,
} from 'lib/components';
import { LOAD_BILLING_INFO } from 'constants/ActionTypes';
import { DASHBOARD_ROUTE } from 'constants/RouteConstants';
import PaymentInformation from 'commonComponents/paymentInformation';
import AchPaymentInformation from 'commonComponents/paymentInformation/AchPaymentInformation.component'; // #genai
import {
  PAYMENT_METHOD_OPTIONS,
  PAYMENT_METHOD_OPTION_IDS,
} from 'commonComponents/paymentInformation/PaymentInformationConstants'; // #genai
import { BillingType, SUCCESS } from 'constants/AppConstants';
import useMediaQuery from 'hooks/useMediaQuery';
import RetryScreen from 'commonComponents/retry-screen';
import AddressInformation from 'commonComponents/addressInformation';
import history from 'store/CreateHistory';
import ValidationMessageBanner from 'commonComponents/validationMessageBanner';
import Banner from 'commonComponents/banner';
import {
  isOrderingDisabledSelector,
  isAchEligibleSelector,
  isMultiplePaymentMethodsEnabledSelector,
  selectPaymentMethodList,
  selectPaymentMethodsLoaded,
  selectPaymentMethodsLoadError,
  selectRecentlyAddedPaymentMethod,
} from 'utils/appSelectors.utils'; // #genai
import AccessDeniedPage from 'commonComponents/accessDeniedPage/accessDeniedPage.component';
import BillingAffiliateView from './components/BillingAffiliateView/BillingAffiliateView.component';
import BillingEmptyState from './components/BillingEmptyState/BillingEmptyState.component'; // #genai
import SavedPaymentMethods from './SavedPaymentMethods/SavedPaymentMethods.component'; // #genai Phase 2 saved-methods list
import SaveDisclaimerDialog from './components/SaveDisclaimerDialog/SaveDisclaimerDialog.component'; // #genai
import BillingInformationStyles from './BillingInformationStyles';
import {
  updatePaymentInfo,
  updateCardPaymentInfo,
  updateAchPaymentInfo,
  updateCreditCardConsent,
  updateAchConsent,
  updateAddressInfo,
  updateAddressInfoErrors,
  submitBillingInfo,
  setSelectedPaymentMethod,
  openSaveDisclaimer,
  closeSaveDisclaimer,
  loadPaymentMethods,
  clearRecentlyAddedPaymentMethod,
} from './BillingInformation.actions';
import {
  showValidationBanner,
  updateMessages,
  shouldShowAffiliateView, // #genai extracted predicate to reduce cognitive complexity
  shouldUpdateElementRefMessages, // #genai extracted predicate to reduce cognitive complexity
} from './BillingInformation.util';
import { getBankAccountInfoErrorTranslationKey, getBankAccountInfoErrorTypeKey } from './BankAccountInfoErrors';

// #genai Extracted from BillingInformation to reduce cognitive complexity (S3776).
// Builds a normalized element ref state object, merging into prevState if it exists.
export function buildElementRefInfoState(prevState, componentRefInfo) {
  const newState = {};
  Object.entries(componentRefInfo).forEach(([key, value]) => {
    newState[key] = {
      id: key,
      elementRef: value.elementRef,
      message: value.message || '',
    };
  });
  return prevState == null ? newState : { ...prevState, ...newState };
}

// #genai Extracted from BillingInformation to reduce cognitive complexity (S3776).
// Derives the submit banner display details from submitInfo state.
// Resolution order so the banner always reflects what actually went wrong:
//   1. Known errorCode (curated FE translation, see BankAccountInfoErrors.js).
//   2. Unknown errorCode + backend errorDescription → show the backend text (it's user-facing
//      per the ev-ecomm-orchestration-service contract).
//   3. No errorCode at all (network failure / fallback string in service/saga) → generic
//      submit_fail_message. We gate on errorCode here because the service-layer fallback
//      string ("Internal Server Error") is not user-friendly and shouldn't surface verbatim.
function resolveSubmitErrorMessage(submitInfo, t) {
  // #genai Phase 2 — resolve by errorType FIRST (guide §4: errorType is the stable contract; new
  // codes like BankCompanyNameMissing may not carry a curated numeric errorCode). Then fall back to
  // the legacy numeric errorCode map, then the backend user-facing errorDescription, then generic.
  const typeKey = getBankAccountInfoErrorTypeKey(submitInfo.errorType);
  if (typeKey) return t(typeKey);
  const translationKey = getBankAccountInfoErrorTranslationKey(submitInfo.errorCode);
  if (translationKey) return t(translationKey);
  if (submitInfo.errorCode && submitInfo.errorMessage) return submitInfo.errorMessage;
  return t('billing_information__submit_fail_message');
}

export function getSubmitInfoDetails(submitInfo, isLoading, t) {
  return {
    ...submitInfo,
    show: submitInfo.showMessage && isLoading === false,
    severity: submitInfo.errorMessage ? 'error' : 'success',
    message: submitInfo.errorMessage
      ? resolveSubmitErrorMessage(submitInfo, t)
      : t('billing_information__submit_success_message'),
  };
}

// #genai Extracted from BillingInformation to reduce cognitive complexity (S3776).
// Determines whether the toggle payment section has any unsaved changes.
export function computeIsTogglePaymentDirty({
  isAchSelected, achPaymentInfo, cardPaymentInfo, updatedACHConsent, updatedCCConsent,
  selectedPaymentMethod, savedSelectedPaymentMethod,
}) {
  const activePaymentInfo = isAchSelected ? achPaymentInfo : cardPaymentInfo;
  const consentChanged = isAchSelected ? updatedACHConsent : updatedCCConsent;
  const paymentMethodSwitched = selectedPaymentMethod !== savedSelectedPaymentMethod;
  return activePaymentInfo.isUpdated || consentChanged || paymentMethodSwitched;
}

// #genai Extracted to reduce cognitive complexity of BillingInformation component.
// Resolves the transient token data needed before dispatching the billing submit action.
export async function resolveTransientTokenData({
  showToggleSection, isAchSelected, achPaymentInfoRef, paymentInfoRef, showCardSection,
}) {
  if (showToggleSection) {
    if (isAchSelected && achPaymentInfoRef.current) {
      const tokenData = await achPaymentInfoRef.current.tokenize();
      // #genai Never reset on tokenization failure. The per-field flags coming back from
      // handleTokenisation (mismatch, routing-invalid, account-invalid) combined with the
      // hosted-field sentinels in Redux let validatePaymentInfo render exact per-field errors:
      // empty → "is required", filled-but-rejected → "is invalid", mismatch → "do not match",
      // filled-and-OK → no error. Wiping sentinels here would turn previously-valid fields
      // into false-positive "is required" errors.
      return tokenData;
    }
    if (!isAchSelected && paymentInfoRef.current) {
      return paymentInfoRef.current.tokenize();
    }
    return {};
  }
  if (showCardSection && paymentInfoRef.current) {
    return paymentInfoRef.current.tokenize();
  }
  return {};
}

// #genai Extracted from BillingInformation to reduce cognitive complexity (SonarQube S3776).
// Returns the Phase 2 render element (empty state or saved-methods list) when the
// multi-payment gate is active and the load has settled; returns null so the component
// falls through to the Phase 1 legacy render otherwise. Behaviour is byte-identical to the
// inline if-block it replaces.
function getPhase2RenderElement({
  isLoading, isPhase2Active, paymentMethodList, isPaymentMethodsLoaded, paymentMethodsLoadError,
}) {
  if (isLoading || !isPhase2Active) return null;
  const hasNoMethods = paymentMethodList.length === 0;
  const isLoadedOrErrored = isPaymentMethodsLoaded || Boolean(paymentMethodsLoadError);
  if (isLoadedOrErrored && hasNoMethods) {
    return <BillingEmptyState />;
  }
  return <SavedPaymentMethods />;
}

// #genai Extracted from BillingInformation to reduce cognitive complexity (SonarQube S3776).
// Collapses the three sequential early-return guards (ordering-disabled → AccessDenied,
// backend init/billing error → Retry, affiliate-view predicate → BillingAffiliateView) into a
// single helper. Returns the JSX to render, or null so the caller falls through to the normal
// render. MUST be called AFTER all hooks in the caller to preserve rules-of-hooks (see the
// caller-side comment).
function renderBillingGuardView({
  isOrderingDisabled, isInError, isLoading, isBillingAffiliate, hasAdministerBillingPermission, onRetry,
}) {
  if (isOrderingDisabled) return <AccessDeniedPage />;
  if (isInError) return <RetryScreen handleRetryButtonClick={onRetry} />;
  if (shouldShowAffiliateView(isLoading, isBillingAffiliate, hasAdministerBillingPermission)) {
    return <BillingAffiliateView />;
  }
  return null;
}

function BillingInformation() {
  const { t } = useTranslation();
  const paymentInfoRef = useRef(null);
  const achPaymentInfoRef = useRef(null); // #genai
  const latestCardPaymentInfoRef = useRef(null); // #genai
  // #genai latestAchPaymentInfoRef was only consumed by the old handleAchPaymentInfoChange
  // (now rewritten to dispatch partial updates). Kept commented per change request.
  // const latestAchPaymentInfoRef = useRef(null); // #genai
  const latestPaymentInfoRef = useRef(null);

  const isMobile = useMediaQuery(640);
  const classes = BillingInformationStyles();
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch({ type: LOAD_BILLING_INFO });
  }, []);

  const {
    isLoading,
    paymentInfo,
    cardPaymentInfo,
    achPaymentInfo,
    ccConsent,
    achConsent,
    paymentInfoErrors,
    cardPaymentInfoErrors,
    achPaymentInfoErrors,
    addressInfo,
    addressInfoErrorsForDisplay,
    paymentInfoErrorsForBanner,
    cardPaymentInfoErrorsForBanner,
    achPaymentInfoErrorsForBanner,
    addressInfoErrorsForBannerDisplay,
    submitInfo,
    achInfo,
    selectedPaymentMethod,
    savedSelectedPaymentMethod,
    updatedCCConsent,
    updatedACHConsent,
    isSaveDisclaimerOpen,
  } = useSelector((
    { billingInformation },
  ) => (billingInformation));

  const {
    billingType,
    hasAdministerBillingPermission,
    isBillingAffiliate,
    isInError,
    // #genai Restore savedCreditCardNumber here once the QA override below is reverted.
  } = useSelector(({
    userData,
  }) => ({
    billingType: userData.billingInformation?.billingType,
    hasAdministerBillingPermission: userData.billingInformation?.hasAdministerBillingPermission,
    isBillingAffiliate: userData.initParameters?.isBillingAffiliate,
    isInError: userData.error?.initParameters || userData.error?.billingInformation,
    // #genai Deferred pending QA-override revert:
    // savedCreditCardNumber: userData.billingInformation?.creditCard?.creditCardNumber,
  }));

  const isOrderingDisabled = useSelector(isOrderingDisabledSelector);
  const isAchEligible = useSelector(isAchEligibleSelector); // #genai
  const isPhase2Active = useSelector(isMultiplePaymentMethodsEnabledSelector); // #genai
  // #genai Phase 2 — unified saved-methods list state (GET /v1/payment-methods).
  const paymentMethodList = useSelector(selectPaymentMethodList);
  const isPaymentMethodsLoaded = useSelector(selectPaymentMethodsLoaded);
  const paymentMethodsLoadError = useSelector(selectPaymentMethodsLoadError);
  const [elementRefInfo, setElementRefInfo] = useState();

  // #genai Phase 2 "Payment method added" success banner. Owned by THIS page-level component
  // (not SavedPaymentMethods) because BillingInformation stays mounted for the entire post-redirect
  // load, whereas the child list/empty-state flip in and out (null → BillingEmptyState →
  // SavedPaymentMethods) as GET /v1/payment-methods resolves. Rendering it here (a) survives that
  // churn and (b) shows the banner even on the empty state. Snapshot the Redux payload at first
  // render so it keeps rendering after we clear Redux; the mount effect clears Redux so the banner
  // shows exactly once (the X still closes it via setAddedMessage(null)).
  const reduxAddedMessage = useSelector(selectRecentlyAddedPaymentMethod);
  const [addedMessage, setAddedMessage] = useState(() => reduxAddedMessage);

  // #genai Phase 2 — load the unified list whenever the multi-payment gate is on. Fires on every
  // mount so returning from /billing-information/add-ach or /add-card refreshes the list. The gate
  // depends on userData.billingInformation (ACH eligibility) hydrated by LOAD_BILLING_INFO above.
  // Per the Phase 2 integration there is no fallback to Phase 1: a server-side 404 surfaces as an
  // error state (retry banner) inside SavedPaymentMethods, not a downgrade to the legacy form.
  useEffect(() => {
    if (isPhase2Active) {
      dispatch(loadPaymentMethods());
    }
  }, [isPhase2Active]);

  useEffect(() => {
    latestPaymentInfoRef.current = paymentInfo;
  }, [paymentInfo]);

  useEffect(() => {
    latestCardPaymentInfoRef.current = cardPaymentInfo;
  }, [cardPaymentInfo]);

  // #genai REMOVED — paired with latestAchPaymentInfoRef which is no longer consumed.
  // useEffect(() => {
  //   latestAchPaymentInfoRef.current = achPaymentInfo;
  // }, [achPaymentInfo]);

  const showToggleSection = hasAdministerBillingPermission && isAchEligible; // #genai
  const showCardSection = hasAdministerBillingPermission && billingType === BillingType.CREDIT_CARD && !isAchEligible; // #genai
  const isAchSelected = selectedPaymentMethod === PAYMENT_METHOD_OPTION_IDS.Ach; // #genai
  const paymentMethodLabel = isAchSelected ? t('payment_information__ach') : t('payment_information__card'); // #genai
  const togglePaymentInfoErrorsForBanner = isAchSelected ? achPaymentInfoErrorsForBanner : cardPaymentInfoErrorsForBanner; // #genai
  const activePaymentInfoErrorsForBanner = showToggleSection ? togglePaymentInfoErrorsForBanner : paymentInfoErrorsForBanner; // #genai

  const handlePaymentInfoChange = useCallback((fieldId, event) => {
    const currentPaymentInfo = latestPaymentInfoRef.current;
    const paymentInformationVal = { ...currentPaymentInfo, isUpdated: true };
    const { value } = event.target;
    const targetId = fieldId || event.target.id;
    paymentInformationVal[targetId] = value;
    dispatch(updatePaymentInfo({ ...paymentInformationVal, isUpdated: true }));
  }, [dispatch]);

  const handleCardPaymentInfoChange = useCallback((fieldId, event) => { // #genai
    const currentPaymentInfo = latestCardPaymentInfoRef.current;
    const paymentInformationVal = { ...currentPaymentInfo, isUpdated: true };
    const { value } = event.target;
    const targetId = fieldId || event.target.id;
    paymentInformationVal[targetId] = value;
    dispatch(updateCardPaymentInfo({ ...paymentInformationVal, isUpdated: true }));
  }, [dispatch]);

  // #genai NEW (stale-ref fix): dispatch only the changed field. The reducer's spread merge
  //   case BILLING_INFO_UPDATE_ACH_PAYMENT_INFO:
  //     return { ...state, achPaymentInfo: { ...state.achPaymentInfo, ...action.achPaymentInfo } }
  // preserves every other field, so we no longer need to read the latest Redux state from a
  // React ref. This removes the race where three back-to-back synchronous calls (e.g. from
  // clearHostedFieldsAndReload) each read the SAME stale snapshot of latestAchPaymentInfoRef
  // and overwrite each other in Redux, leaving only the last field actually cleared.
  const handleAchPaymentInfoChange = useCallback((fieldId, event) => { // #genai
    const { value } = event.target;
    const targetId = fieldId || event.target.id;
    dispatch(updateAchPaymentInfo({ [targetId]: value, isUpdated: true }));

    // #genai OLD (commented for reference) — read full snapshot from ref → mutate → dispatch
    // whole object. Bug: latestAchPaymentInfoRef.current is updated by a useEffect that runs
    // AFTER React renders, so back-to-back calls see the same stale value and produce
    // dispatches that overwrite each other.
    // const currentPaymentInfo = latestAchPaymentInfoRef.current;
    // const paymentInformationVal = { ...currentPaymentInfo, isUpdated: true };
    // const { value } = event.target;
    // const targetId = fieldId || event.target.id;
    // paymentInformationVal[targetId] = value;
    // dispatch(updateAchPaymentInfo({ ...paymentInformationVal, isUpdated: true }));
  }, [dispatch]);

  // #genai REMOVED — no longer needed after the generalized fix above. The three
  // handleChange calls inside AchPaymentInformation.clearHostedFieldsAndReload now apply
  // correctly because each dispatch is a partial that the reducer merges with the current
  // Redux state (not a stale React snapshot). Kept commented per change request.
  // const handleClearAchBankFields = useCallback(() => {
  //   dispatch(updateAchPaymentInfo({
  //     routingNumber: '',
  //     accountNumber: '',
  //     accountNumberConfirm: '',
  //   }));
  // }, [dispatch]);

  const handleAddressInfoChange = (fieldId, value) => dispatch(updateAddressInfo(fieldId, value));

  const handleAddressInfoErrorChange = (fieldId, fieldErrorValue, bannerErrorValue) => dispatch(
    updateAddressInfoErrors(fieldId, fieldErrorValue, bannerErrorValue),
  );

  const handleConsentCheckboxChange = () => dispatch(updateCreditCardConsent(!ccConsent));
  const handleAchConsentCheckboxChange = () => dispatch(updateAchConsent(!achConsent)); // #genai

  // #genai delegates to module-level helper to reduce cognitive complexity
  const isTogglePaymentDirty = () => computeIsTogglePaymentDirty({
    isAchSelected,
    achPaymentInfo,
    cardPaymentInfo,
    updatedACHConsent,
    updatedCCConsent,
    selectedPaymentMethod,
    savedSelectedPaymentMethod,
  });

  const executeBillingInfoSubmission = async () => { // #genai
    // #genai scrollToTop is now driven from the saga after validation/error/success dispatches
    // so the viewport reaches the top exactly when the banner (errors or success) is rendered.
    const transientTokenData = await resolveTransientTokenData({
      showToggleSection, isAchSelected, achPaymentInfoRef, paymentInfoRef, showCardSection,
    });
    dispatch(submitBillingInfo(transientTokenData));
  };

  const handleBillingInfoSubmission = async (event) => {
    event.preventDefault();
    if (showToggleSection && isTogglePaymentDirty()) {
      dispatch(openSaveDisclaimer());
      return;
    }
    await executeBillingInfoSubmission();
  };

  const handleSaveDisclaimerConfirm = async () => {
    dispatch(closeSaveDisclaimer());
    await executeBillingInfoSubmission();
  };

  const handleSaveDisclaimerCancel = () => {
    dispatch(closeSaveDisclaimer());
  };

  useEffect(() => {
    // #genai Optional chaining on resetMicroform replaces the 4th && existence-check,
    // keeping the condition to 3 clauses and reducing cognitive complexity (SonarQube S3776).
    if (submitInfo.errorMessage && showToggleSection && isAchSelected) {
      achPaymentInfoRef.current?.resetMicroform();
    }
  }, [submitInfo.errorMessage, showToggleSection, isAchSelected]);

  // #genai Consume the success-banner payload exactly once: clear it from Redux on first mount.
  // Empty deps are intentional — the local `addedMessage` snapshot (captured above) keeps the
  // banner rendered after this clear lands, so it survives the child list/empty-state remounts.
  useEffect(() => {
    if (reduxAddedMessage) {
      dispatch(clearRecentlyAddedPaymentMethod());
    }
  }, []);

  // #genai Rules-of-hooks: the guard call MUST come AFTER every hook above (useEffect / useCallback
  // / useState / useSelector) so the hook count is identical on every render. This previously lived
  // as three separate `if` returns higher up — when a backend failure flipped isInError true on a
  // later render, the component bailed out before the remaining hooks ran, throwing "Rendered fewer
  // hooks than expected" and tearing down the root saga watchers. Consolidating the guards into a
  // single helper call (still positioned below all hooks) preserves the fix and reduces the
  // component's cognitive complexity (SonarQube S3776).
  const guardView = renderBillingGuardView({
    isOrderingDisabled,
    isInError,
    isLoading,
    isBillingAffiliate,
    hasAdministerBillingPermission,
    onRetry: () => dispatch({ type: LOAD_BILLING_INFO }),
  });
  if (guardView) return guardView;

  const updateElementRefInfo = (componentRefInfo) => { // #genai delegates to standalone helper to reduce cognitive complexity
    setElementRefInfo((prevState) => buildElementRefInfoState(prevState, componentRefInfo));
  };

  // #genai Predicate extracted to BillingInformation.util to reduce cognitive complexity (SonarQube S3776)
  if (shouldUpdateElementRefMessages(elementRefInfo, addressInfoErrorsForBannerDisplay, activePaymentInfoErrorsForBanner)) {
    setElementRefInfo((prevState) => {
      const newState = updateMessages(prevState, addressInfoErrorsForBannerDisplay, activePaymentInfoErrorsForBanner);
      return newState;
    });
  }

  const submitInfoDetails = getSubmitInfoDetails(submitInfo, isLoading, t); // #genai delegates to standalone helper

  // #genai Phase 2 (ENABLE_MULTI_PAYMENT_METHODS): two states render on /billing-information itself:
  //   1. No saved methods (either genuinely empty OR the list call failed / feature is unavailable
  //      server-side) → empty state (Figma 269:20209). We treat a load error the same as "empty" so
  //      the page always shows the correct Figma UX — the user can still open Add Payment Method
  //      and try to add one; if the backend genuinely doesn't support Phase 2 yet, that flow will
  //      surface the appropriate error at save time.
  //   2. One or more saved methods → SavedPaymentMethods list (expand / make-default / remove).
  // Both render an "Add Payment Method" button that opens a payment-type menu (Bank / Credit Card)
  // and routes to the dedicated add-ACH / add-card pages — there is no inline picker anymore.
  // Data comes from GET /v1/payment-methods (see MYEV_UI_INTEGRATION_GUIDE §2.3). There is NO
  // fallback to the Phase 1 form.
  // #genai Predicate extracted above the component to reduce cognitive complexity (SonarQube S3776).
  // #genai Phase 2 success banner (Figma 331:26943 card / 331:27171 ACH): rendered at the page
  // level so it appears above whichever child renders (saved list OR empty state) and is immune to
  // their mount/unmount churn during the GET /v1/payment-methods load. Subtitle = "{accountType}
  // ending in {suffix} has been added."; the "Set as your default…" line only shows when the user
  // left "Make default" ticked (message.isDefault).
  const paymentAddedBanner = addedMessage && (
    <Banner
      severity={SUCCESS}
      className={classes.successBanner}
      title={t('billing_information__pm_added_title')}
      handleAlertClose={() => setAddedMessage(null)}
      message={(
        <>
          <div>
            {t('billing_information__pm_added_subtitle', {
              accountType: addedMessage.accountTypeLabel,
              suffix: addedMessage.accountSuffix,
            })}
          </div>
          {addedMessage.isDefault && (
            <div>{t('billing_information__pm_added_message')}</div>
          )}
        </>
      )}
    />
  );

  const phase2Element = getPhase2RenderElement({
    isLoading, isPhase2Active, paymentMethodList, isPaymentMethodsLoaded, paymentMethodsLoadError,
  });
  if (phase2Element) {
    return (
      <>
        {paymentAddedBanner}
        {phase2Element}
      </>
    );
  }

  const renderPaymentMethodToggle = () => ( // #genai
    <EVGrid className={classes.paymentMethodContainer} item>
      <EVInputLabel className={classes.paymentMethodLabel}>
        {t('payment_information__payment_method')}
      </EVInputLabel>
      <EVGrid container className={classes.paymentMethodOptions}>
        {PAYMENT_METHOD_OPTIONS.map(({ id }) => {
          const isSelected = selectedPaymentMethod === id;
          return (
            <EVGrid
              key={id}
              container
              alignItems="center"
              className={`${classes.paymentMethodOption} ${isSelected ? classes.paymentMethodOptionSelected : ''}`}
              onClick={() => dispatch(setSelectedPaymentMethod(id))}
            >
              <EVRadio
                checked={isSelected}
                onChange={() => dispatch(setSelectedPaymentMethod(id))}
                color="primary"
                style={{ padding: 0 }}
                name="paymentMethod"
                value={id}
              />
              <EVInputLabel style={{ fontSize: '16px', fontWeight: 400, margin: 0 }}>
                {t(`payment_information__${id}`)}
              </EVInputLabel>
            </EVGrid>
          );
        })}
      </EVGrid>
    </EVGrid>
  );

  return (
    <>
      {!isLoading && showValidationBanner(activePaymentInfoErrorsForBanner, addressInfoErrorsForBannerDisplay)
        && (
          <ValidationMessageBanner
            title={t('validation_message_banner__title')}
            heading={t('validation_message_banner__error_heading')}
            elementRefInfo={elementRefInfo}
          />
        )}
      {submitInfoDetails.show && (
        <Banner
          severity={submitInfoDetails.severity}
          message={submitInfoDetails.message}
        />
      )}
      {!isLoading && !isMobile && <EVTypography className={classes.pageHeader}>{t('billing_information__view_and_edit_info')}</EVTypography>}
      <form onSubmit={handleBillingInfoSubmission} className={classes.formContainer}>
        {(showToggleSection || showCardSection) && (
          <EVPaper className={isLoading ? classes.paperLoading : classes.paper}>
            {!isLoading && isMobile && (
              <EVTypography
                test-id="pageHeaderMobile"
                className={classes.pageHeader}
              >
                {t('billing_information__view_and_edit_info')}
              </EVTypography>
            )}
            <EVGrid container direction="column" className={classes.paymentInformationContainer}>
              {!isLoading && (
                <EVTypography className={classes.paymentInformationTitle}>
                  {showToggleSection
                    ? `${t('billing_information__payment_information')} ${paymentMethodLabel}`
                    : t('billing_information__payment_information')}
                </EVTypography>
              )}
              {showToggleSection && !isLoading && renderPaymentMethodToggle()}
              {showToggleSection && isAchSelected && (
                <AchPaymentInformation
                  key={submitInfo.showMessage ? 'ach-submitted' : 'ach-initial'}
                  isLoading={isLoading}
                  paymentInfo={achPaymentInfo}
                  handleChange={handleAchPaymentInfoChange}
                  /* #genai REMOVED — handleAchPaymentInfoChange now dispatches partial
                     updates, so the three handleChange calls in the child's
                     clearHostedFieldsAndReload merge correctly via the reducer.
                  clearBankFields={handleClearAchBankFields}
                  */
                  achConsent={achConsent}
                  handleConsentCheckboxChange={handleAchConsentCheckboxChange}
                  paymentInfoErrors={achPaymentInfoErrors}
                  ref={achPaymentInfoRef}
                  displayCardChargeMessage
                  updateElementRefs={updateElementRefInfo}
                  achInfo={achInfo}
                />
              )}
              {showToggleSection && !isAchSelected && (
                <PaymentInformation
                  key={submitInfo.showMessage ? 'card-submitted' : 'card-initial'}
                  isLoading={isLoading}
                  paymentInfo={cardPaymentInfo}
                  handleChange={handleCardPaymentInfoChange}
                  ccConsent={ccConsent}
                  handleConsentCheckboxChange={handleConsentCheckboxChange}
                  paymentInfoErrors={cardPaymentInfoErrors}
                  ref={paymentInfoRef}
                  displayCardChargeMessage
                  updateElementRefs={updateElementRefInfo}
                />
              )}
              {showCardSection && (
                <PaymentInformation
                  key={submitInfo.showMessage ? 'submitted' : 'initial'}
                  isLoading={isLoading}
                  paymentInfo={paymentInfo}
                  handleChange={handlePaymentInfoChange}
                  ccConsent={ccConsent}
                  handleConsentCheckboxChange={handleConsentCheckboxChange}
                  paymentInfoErrors={paymentInfoErrors}
                  ref={paymentInfoRef}
                  displayCardChargeMessage
                  updateElementRefs={updateElementRefInfo}
                />
              )}
            </EVGrid>
          </EVPaper>
        )}
        <EVPaper className={classes.paper}>
          {!isLoading && isMobile && !(showToggleSection || showCardSection) && (
            <EVTypography
              test-id="pageHeaderMobile"
              className={classes.pageHeader}
            >
              {t('billing_information__view_and_edit_info')}
            </EVTypography>
          )}
          <EVGrid container direction="column" className={classes.addressInformationContainer}>
            {!isLoading && (
              <EVGrid container item className={classes.addressTitleContainer} direction="column">
                <EVTypography className={classes.addressInformationTitle}>{t('billing_information__address_information__title')}</EVTypography>
                <EVTypography className={classes.addressInformationMessage}>{t('billing_information__address_information__message')}</EVTypography>
              </EVGrid>
            )}
            <AddressInformation
              isLoading={isLoading}
              addressInfo={addressInfo}
              handleChange={handleAddressInfoChange}
              addressInfoErrors={addressInfoErrorsForDisplay}
              handleErrorChange={handleAddressInfoErrorChange}
              onCancel={() => history.replace(DASHBOARD_ROUTE)}
              updateElementRefs={updateElementRefInfo}
            />
          </EVGrid>
        </EVPaper>
      </form>
      {showToggleSection && (
        <SaveDisclaimerDialog
          open={isSaveDisclaimerOpen}
          onConfirm={handleSaveDisclaimerConfirm}
          onCancel={handleSaveDisclaimerCancel}
        />
      )}
    </>
  );
}

export default BillingInformation;
